/**
 * Advisory KYC review assistant panel (staff-only).
 *
 * Fetches the server's deterministic, in-house review signals for a case and
 * renders them as a risk semaphore + a checklist of verdicts. It surfaces ONLY
 * verdicts (no PII) — so an agent who cannot decrypt CURP/RFC still sees the
 * consistency results. Purely advisory: the reviewer decides; this only guides.
 */
import { useEffect, useState } from 'react';

import workflowService, {
  type KycReview,
  type KycSignal,
} from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

interface Props {
  caseId: string;
}

const RISK_STYLE: Record<KycReview['risk_level'], { bg: string; fg: string; label: string }> = {
  low: { bg: colors.successBg, fg: colors.success, label: 'Low risk' },
  medium: { bg: '#FEF9C3', fg: '#713F12', label: 'Medium — review' },
  high: { bg: colors.dangerBg, fg: colors.danger, label: 'High — needs attention' },
};

const STATUS_GLYPH: Record<KycSignal['status'], { icon: string; color: string }> = {
  ok: { icon: '✓', color: colors.success },
  warn: { icon: '⚠', color: '#CA8A04' },
  fail: { icon: '✗', color: colors.danger },
  na: { icon: '–', color: colors.mutedSoft },
};

export default function KycReviewAssistant({ caseId }: Readonly<Props>): JSX.Element {
  const [review, setReview] = useState<KycReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    workflowService
      .getReviewAssistant(caseId)
      .then((r) => {
        if (active) {
          setReview(r);
          setError(null);
        }
      })
      .catch((e) => {
        if (active) setError(getApiErrorMessage(e, 'Could not load the review assistant.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [caseId]);

  return (
    <section
      className="rounded-2xl p-5 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: colors.muted }}>
          🤖 Review assistant
        </h2>
        {review ? (
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{
              background: RISK_STYLE[review.risk_level].bg,
              color: RISK_STYLE[review.risk_level].fg,
            }}
          >
            {RISK_STYLE[review.risk_level].label}
          </span>
        ) : null}
      </div>

      <p className="text-xs mb-3" style={{ color: colors.mutedSoft }}>
        Automated, in-house consistency checks. Advisory only — you make the decision.
      </p>

      {loading ? (
        <p className="text-sm" style={{ color: colors.muted }}>
          Running checks…
        </p>
      ) : null}

      {error ? (
        <div
          className="rounded-lg p-3 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          {error}
        </div>
      ) : null}

      {review ? (
        <ul className="space-y-2">
          {review.signals.map((s) => {
            const glyph = STATUS_GLYPH[s.status];
            return (
              <li key={s.code} className="flex items-start gap-2 text-sm">
                <span
                  aria-hidden="true"
                  style={{ color: glyph.color, fontWeight: 700, lineHeight: '1.35' }}
                >
                  {glyph.icon}
                </span>
                <span style={{ color: colors.ink }}>
                  <strong>{s.label}</strong>
                  <span style={{ color: colors.mutedSoft }}> — {s.detail}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
